import { CanvasTexture, NearestFilter, SRGBColorSpace } from 'three';
import { BlockType } from '../types';

const TILE = 32;
/** Atlas laid out as a 4×2 grid of 32×32 tiles. */
const COLS = 4;
const ROWS = 2;

/** Atlas UV tile index per block type (matches draw order below). */
export const BLOCK_ATLAS_INDEX: Record<BlockType, number> = {
  [BlockType.Air]: 0,
  [BlockType.Dirt]: 0,
  [BlockType.Stone]: 1,
  [BlockType.Iron]: 2,
  [BlockType.Gold]: 3,
  [BlockType.SurfacePortal]: 4,
  [BlockType.ExitDoor]: 5,
  [BlockType.Bedrock]: 6,
  [BlockType.Shop]: 7,
};

export const BLOCK_ATLAS_COLS = COLS;
export const BLOCK_ATLAS_ROWS = ROWS;

/**
 * Builds a nearest-filtered block atlas with hand-painted procedural tiles.
 * Iron / gold get vein + sparkle detail so ores read clearly in-world.
 */
export function createBlockAtlasTexture(): CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = TILE * COLS;
  canvas.height = TILE * ROWS;
  const ctx = canvas.getContext('2d')!;

  drawDirt(ctx, 0, 0);
  drawStone(ctx, TILE, 0);
  drawIron(ctx, TILE * 2, 0);
  drawGold(ctx, TILE * 3, 0);
  drawPortal(ctx, 0, TILE);
  drawExit(ctx, TILE, TILE);
  drawBedrock(ctx, TILE * 2, TILE);
  drawShop(ctx, TILE * 3, TILE);

  const texture = new CanvasTexture(canvas);
  texture.magFilter = NearestFilter;
  texture.minFilter = NearestFilter;
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function fillNoise(
  ctx: CanvasRenderingContext2D,
  ox: number,
  oy: number,
  base: string,
  variance: [number, number, number],
  density: number,
): void {
  ctx.fillStyle = base;
  ctx.fillRect(ox, oy, TILE, TILE);
  const image = ctx.getImageData(ox, oy, TILE, TILE);
  const d = image.data;
  for (let i = 0; i < d.length; i += 4) {
    if (Math.random() > density) continue;
    d[i] = clampByte(d[i]! + (Math.random() - 0.5) * variance[0]);
    d[i + 1] = clampByte(d[i + 1]! + (Math.random() - 0.5) * variance[1]);
    d[i + 2] = clampByte(d[i + 2]! + (Math.random() - 0.5) * variance[2]);
  }
  ctx.putImageData(image, ox, oy);
}

function clampByte(v: number): number {
  return Math.max(0, Math.min(255, v | 0));
}

function drawDirt(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  // Warm packed earth base
  fillNoise(ctx, ox, oy, '#6a4220', [30, 22, 14], 0.92);

  // Soft soil clumps — lighter / darker patches
  for (let i = 0; i < 9; i++) {
    const x = ox + ((Math.random() * (TILE - 7)) | 0);
    const y = oy + ((Math.random() * (TILE - 7)) | 0);
    const w = 3 + ((Math.random() * 5) | 0);
    const h = 2 + ((Math.random() * 4) | 0);
    ctx.fillStyle =
      Math.random() > 0.45 ? 'rgba(120, 82, 42, 0.5)' : 'rgba(52, 32, 16, 0.45)';
    ctx.fillRect(x, y, w, h);
  }

  // Fine grit
  for (let i = 0; i < 30; i++) {
    const x = ox + ((Math.random() * TILE) | 0);
    const y = oy + ((Math.random() * TILE) | 0);
    ctx.fillStyle = Math.random() > 0.5 ? '#8a6238' : '#4a2f18';
    ctx.fillRect(x, y, 1, 1);
  }

  // Embedded pebbles
  for (let i = 0; i < 10; i++) {
    const x = ox + 1 + ((Math.random() * (TILE - 4)) | 0);
    const y = oy + 1 + ((Math.random() * (TILE - 4)) | 0);
    const cool = Math.random() > 0.55;
    ctx.fillStyle = cool ? '#5a5448' : '#3a2818';
    ctx.fillRect(x, y, 2, 2);
    ctx.fillStyle = cool ? '#8a8478' : '#7a5830';
    ctx.fillRect(x, y, 1, 1);
  }

  // Sparse root / fiber flecks
  ctx.lineCap = 'square';
  ctx.lineWidth = 1;
  for (let i = 0; i < 5; i++) {
    const x0 = ox + 2 + Math.random() * (TILE - 4);
    const y0 = oy + 2 + Math.random() * (TILE - 4);
    ctx.strokeStyle = Math.random() > 0.4 ? 'rgba(40, 26, 12, 0.55)' : 'rgba(90, 60, 28, 0.4)';
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x0 + (Math.random() - 0.5) * 8, y0 + 2 + Math.random() * 6);
    ctx.stroke();
  }

  // Soft face depth
  ctx.fillStyle = 'rgba(150, 110, 60, 0.16)';
  ctx.fillRect(ox + 1, oy + 1, TILE - 2, 2);
  ctx.fillRect(ox + 1, oy + 1, 2, TILE - 2);
  ctx.fillStyle = 'rgba(28, 16, 8, 0.2)';
  ctx.fillRect(ox + 1, oy + TILE - 3, TILE - 2, 2);
  ctx.fillRect(ox + TILE - 3, oy + 1, 2, TILE - 2);
}

function drawStone(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  // Cool grey host with warm undertone so it sits between dirt and iron
  fillNoise(ctx, ox, oy, '#6e7682', [22, 20, 24], 0.92);

  // Irregular mineral patches (lighter / darker rock facets)
  for (let i = 0; i < 10; i++) {
    const x = ox + ((Math.random() * (TILE - 6)) | 0);
    const y = oy + ((Math.random() * (TILE - 6)) | 0);
    const w = 3 + ((Math.random() * 5) | 0);
    const h = 2 + ((Math.random() * 4) | 0);
    ctx.fillStyle =
      Math.random() > 0.45 ? 'rgba(156, 164, 176, 0.55)' : 'rgba(52, 58, 68, 0.5)';
    ctx.fillRect(x, y, w, h);
  }

  // Speckled grit
  for (let i = 0; i < 28; i++) {
    const x = ox + ((Math.random() * TILE) | 0);
    const y = oy + ((Math.random() * TILE) | 0);
    ctx.fillStyle =
      Math.random() > 0.5 ? '#9aa3b0' : '#4a515c';
    ctx.fillRect(x, y, 1, 1);
  }

  // Angular fissures — short segmented cracks, not freehand scribbles
  ctx.lineCap = 'square';
  ctx.lineWidth = 1;
  for (let i = 0; i < 7; i++) {
    let x = ox + 2 + Math.random() * (TILE - 4);
    let y = oy + 2 + Math.random() * (TILE - 4);
    ctx.strokeStyle = Math.random() > 0.4 ? 'rgba(32, 36, 44, 0.7)' : 'rgba(28, 32, 40, 0.45)';
    ctx.beginPath();
    ctx.moveTo(x, y);
    const segs = 2 + ((Math.random() * 3) | 0);
    for (let s = 0; s < segs; s++) {
      x += (Math.random() - 0.5) * 10;
      y += 2 + Math.random() * 5;
      ctx.lineTo(
        Math.max(ox + 1, Math.min(ox + TILE - 1, x)),
        Math.max(oy + 1, Math.min(oy + TILE - 1, y)),
      );
    }
    ctx.stroke();
  }

  // Soft face highlight + shadow so the tile reads as carved rock
  ctx.fillStyle = 'rgba(200, 208, 220, 0.18)';
  ctx.fillRect(ox + 1, oy + 1, TILE - 2, 2);
  ctx.fillRect(ox + 1, oy + 1, 2, TILE - 2);
  ctx.fillStyle = 'rgba(20, 24, 30, 0.22)';
  ctx.fillRect(ox + 1, oy + TILE - 3, TILE - 2, 2);
  ctx.fillRect(ox + TILE - 3, oy + 1, 2, TILE - 2);
}

function drawIron(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  // Dark blue-slate host, cooler than plain stone
  fillNoise(ctx, ox, oy, '#3f4754', [16, 18, 22], 0.9);

  // Host rock grit
  for (let i = 0; i < 20; i++) {
    const x = ox + ((Math.random() * TILE) | 0);
    const y = oy + ((Math.random() * TILE) | 0);
    ctx.fillStyle = Math.random() > 0.5 ? '#5a6574' : '#2a303a';
    ctx.fillRect(x, y, 1, 1);
  }

  // Layered metallic veins: dark underlay then bright silver
  ctx.lineCap = 'square';
  for (let i = 0; i < 5; i++) {
    let x = ox + 3 + Math.random() * (TILE - 6);
    let y = oy + 3 + Math.random() * (TILE - 6);
    const segs = 2 + ((Math.random() * 3) | 0);
    const path: [number, number][] = [[x, y]];
    for (let s = 0; s < segs; s++) {
      x += (Math.random() - 0.35) * 12;
      y += (Math.random() - 0.4) * 12;
      path.push([
        Math.max(ox + 2, Math.min(ox + TILE - 2, x)),
        Math.max(oy + 2, Math.min(oy + TILE - 2, y)),
      ]);
    }
    ctx.strokeStyle = 'rgba(40, 48, 60, 0.85)';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(path[0]![0], path[0]![1]);
    for (let p = 1; p < path.length; p++) ctx.lineTo(path[p]![0], path[p]![1]);
    ctx.stroke();

    ctx.strokeStyle = Math.random() > 0.35 ? '#c8d4e6' : '#9aacc4';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(path[0]![0], path[0]![1]);
    for (let p = 1; p < path.length; p++) ctx.lineTo(path[p]![0], path[p]![1]);
    ctx.stroke();
  }

  // Ore nodules — small metallic clusters
  for (let i = 0; i < 8; i++) {
    const x = ox + 2 + ((Math.random() * (TILE - 6)) | 0);
    const y = oy + 2 + ((Math.random() * (TILE - 6)) | 0);
    ctx.fillStyle = '#6e7e94';
    ctx.fillRect(x, y, 3, 3);
    ctx.fillStyle = Math.random() > 0.4 ? '#e4eef8' : '#a8b8cc';
    ctx.fillRect(x + 1, y + 1, 2, 2);
  }

  // Bright flecks
  for (let i = 0; i < 14; i++) {
    const x = ox + 2 + ((Math.random() * (TILE - 4)) | 0);
    const y = oy + 2 + ((Math.random() * (TILE - 4)) | 0);
    ctx.fillStyle = Math.random() > 0.5 ? '#f0f6ff' : '#8fa0b8';
    ctx.fillRect(x, y, 1, 1);
  }

  // Cool steel rim
  ctx.strokeStyle = 'rgba(170, 200, 240, 0.4)';
  ctx.lineWidth = 2;
  ctx.strokeRect(ox + 1, oy + 1, TILE - 2, TILE - 2);
}

function drawGold(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  // Warm brown host rock
  fillNoise(ctx, ox, oy, '#4e3c22', [18, 14, 10], 0.88);

  // Earth grit
  for (let i = 0; i < 18; i++) {
    const x = ox + ((Math.random() * TILE) | 0);
    const y = oy + ((Math.random() * TILE) | 0);
    ctx.fillStyle = Math.random() > 0.5 ? '#6a5230' : '#3a2c18';
    ctx.fillRect(x, y, 1, 1);
  }

  // Branching gold veins with dark underlay
  ctx.lineCap = 'round';
  for (let i = 0; i < 5; i++) {
    let x = ox + 4 + Math.random() * (TILE - 8);
    let y = oy + 4 + Math.random() * (TILE - 8);
    const segs = 2 + ((Math.random() * 3) | 0);
    const path: [number, number][] = [[x, y]];
    for (let s = 0; s < segs; s++) {
      x += (Math.random() - 0.25) * 11;
      y += (Math.random() - 0.45) * 11;
      path.push([
        Math.max(ox + 2, Math.min(ox + TILE - 2, x)),
        Math.max(oy + 2, Math.min(oy + TILE - 2, y)),
      ]);
    }
    ctx.strokeStyle = 'rgba(60, 40, 12, 0.75)';
    ctx.lineWidth = 3.5;
    ctx.beginPath();
    ctx.moveTo(path[0]![0], path[0]![1]);
    for (let p = 1; p < path.length; p++) ctx.lineTo(path[p]![0], path[p]![1]);
    ctx.stroke();

    ctx.strokeStyle = Math.random() > 0.4 ? '#f0c43a' : '#d4a028';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(path[0]![0], path[0]![1]);
    for (let p = 1; p < path.length; p++) ctx.lineTo(path[p]![0], path[p]![1]);
    ctx.stroke();
  }

  // Chunky gold nuggets
  for (let i = 0; i < 7; i++) {
    const x = ox + 2 + ((Math.random() * (TILE - 7)) | 0);
    const y = oy + 2 + ((Math.random() * (TILE - 7)) | 0);
    const s = 2 + ((Math.random() * 2) | 0);
    ctx.fillStyle = '#b87818';
    ctx.fillRect(x, y, s + 1, s + 1);
    ctx.fillStyle = Math.random() > 0.45 ? '#ffe566' : '#ffb020';
    ctx.fillRect(x, y, s, s);
    ctx.fillStyle = 'rgba(255, 245, 180, 0.85)';
    ctx.fillRect(x, y, 1, 1);
  }

  // Hot sparkles
  for (let i = 0; i < 12; i++) {
    const x = ox + 2 + ((Math.random() * (TILE - 4)) | 0);
    const y = oy + 2 + ((Math.random() * (TILE - 4)) | 0);
    ctx.fillStyle = Math.random() > 0.5 ? '#fff8c8' : '#ff9020';
    ctx.fillRect(x, y, 1, 1);
  }

  // Warm amber rim
  ctx.strokeStyle = 'rgba(255, 200, 60, 0.5)';
  ctx.lineWidth = 2;
  ctx.strokeRect(ox + 1, oy + 1, TILE - 2, TILE - 2);
}

function drawPortal(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  const g = ctx.createLinearGradient(ox, oy, ox + TILE, oy + TILE);
  g.addColorStop(0, '#1f6b4a');
  g.addColorStop(0.5, '#3ecf8e');
  g.addColorStop(1, '#9bffe0');
  ctx.fillStyle = g;
  ctx.fillRect(ox, oy, TILE, TILE);
  ctx.strokeStyle = '#e8fff6';
  ctx.lineWidth = 2;
  ctx.strokeRect(ox + 4, oy + 4, TILE - 8, TILE - 8);
}

function drawExit(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  // Warm stone frame
  fillNoise(ctx, ox, oy, '#4a3428', [18, 14, 10], 0.75);

  // Outer amber rim
  ctx.fillStyle = '#e07028';
  ctx.fillRect(ox + 2, oy + 2, TILE - 4, TILE - 4);

  // Dark inset
  ctx.fillStyle = '#1a0c06';
  ctx.fillRect(ox + 5, oy + 4, TILE - 10, TILE - 8);

  // Glowing portal opening
  const g = ctx.createLinearGradient(ox + 7, oy + 5, ox + TILE - 7, oy + TILE - 4);
  g.addColorStop(0, '#ffb060');
  g.addColorStop(0.45, '#ff6a22');
  g.addColorStop(1, '#c02808');
  ctx.fillStyle = g;
  ctx.fillRect(ox + 7, oy + 5, TILE - 14, TILE - 9);

  // Vertical light shafts
  ctx.fillStyle = 'rgba(255, 230, 170, 0.35)';
  ctx.fillRect(ox + 10, oy + 6, 3, TILE - 12);
  ctx.fillRect(ox + 19, oy + 6, 3, TILE - 12);

  // Arch highlight at top of opening
  ctx.fillStyle = 'rgba(255, 220, 140, 0.7)';
  ctx.fillRect(ox + 7, oy + 5, TILE - 14, 3);

  // Gold studs on frame
  ctx.fillStyle = '#ffd080';
  ctx.fillRect(ox + 3, oy + 3, 3, 3);
  ctx.fillRect(ox + TILE - 6, oy + 3, 3, 3);
  ctx.fillRect(ox + 3, oy + TILE - 6, 3, 3);
  ctx.fillRect(ox + TILE - 6, oy + TILE - 6, 3, 3);

  // Downward chevron (go deeper)
  ctx.fillStyle = '#fff2c8';
  ctx.beginPath();
  ctx.moveTo(ox + 16, oy + 20);
  ctx.lineTo(ox + 11, oy + 14);
  ctx.lineTo(ox + 21, oy + 14);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(ox + 16, oy + 25);
  ctx.lineTo(ox + 12, oy + 20);
  ctx.lineTo(ox + 20, oy + 20);
  ctx.closePath();
  ctx.fill();
}

function drawBedrock(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  fillNoise(ctx, ox, oy, '#1a1d24', [10, 10, 12], 0.7);
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  for (let y = 0; y < TILE; y += 4) {
    ctx.fillRect(ox, oy + y, TILE, 1);
  }
}

function drawShop(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
  // Warm stall wood
  fillNoise(ctx, ox, oy, '#5a3a1e', [22, 16, 10], 0.8);

  // Awning stripe
  ctx.fillStyle = '#c43a2a';
  ctx.fillRect(ox + 2, oy + 2, TILE - 4, 8);
  ctx.fillStyle = '#f0e6d0';
  for (let i = 0; i < 4; i++) {
    ctx.fillRect(ox + 4 + i * 7, oy + 2, 3, 8);
  }

  // Counter
  ctx.fillStyle = '#3a2818';
  ctx.fillRect(ox + 3, oy + 18, TILE - 6, 10);

  // Coin stack
  ctx.fillStyle = '#f0c43a';
  ctx.fillRect(ox + 8, oy + 12, 6, 6);
  ctx.fillRect(ox + 10, oy + 10, 6, 6);
  ctx.fillStyle = '#ffe080';
  ctx.fillRect(ox + 11, oy + 11, 4, 2);

  // Price tag
  ctx.fillStyle = '#e8eef5';
  ctx.fillRect(ox + 20, oy + 13, 8, 6);
  ctx.fillStyle = '#c43a2a';
  ctx.fillRect(ox + 22, oy + 15, 4, 2);
}
